/** HTTP calls to the game server (same origin). */
import type { HttpErrorResponse, RoomInfoResponse, SessionResponse } from "@handshake/protocol";

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function call<T>(method: "GET" | "POST", path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers: body === undefined ? undefined : { "content-type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError("network", "Cannot reach the game server. Check your connection.", 0);
  }
  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    // not JSON
  }
  if (!res.ok) {
    const e = (json as HttpErrorResponse | null)?.error;
    throw new ApiError(
      e?.code ?? "http",
      e?.message ?? `Request failed (${res.status}).`,
      res.status,
    );
  }
  return json as T;
}

export function createRoom(name: string, colour: string): Promise<SessionResponse> {
  return call("POST", "/api/rooms", { name, colour });
}

export function joinRoom(code: string, name: string, colour: string): Promise<SessionResponse> {
  return call("POST", `/api/rooms/${encodeURIComponent(code)}/join`, { name, colour });
}

export function roomInfo(code: string): Promise<RoomInfoResponse> {
  return call("GET", `/api/rooms/${encodeURIComponent(code)}`);
}

// Dev tools (DEV_TOOLS=1 only).

export interface DevStatus {
  readonly devTools: true;
  readonly timeScale: number;
}

export async function devStatus(): Promise<DevStatus | null> {
  try {
    return await call<DevStatus>("GET", "/api/dev/status");
  } catch {
    return null;
  }
}

export function devAddBots(
  code: string,
  count: number,
  strategy = "heuristic",
  paceMs: [number, number] = [400, 1500],
): Promise<{ players: { id: string; name: string }[] }> {
  return call("POST", `/api/dev/rooms/${code}/bots`, { count, strategy, paceMs });
}

export function devEndPhase(code: string): Promise<{ ok: true }> {
  return call("POST", `/api/dev/rooms/${code}/end-phase`, {});
}
